import { apiService } from "./api.service";
import { Language } from "types/index";
import { Lookup } from "types/lookup";
import { PaginatedResponse } from "types/index";
import api_no_token_Service from "./api_no_token.service";
import { normalizePaginatedResponse } from "utils/pagination";
import { unwrapEntity, unwrapOrThrow } from "utils/apiResponse";

export interface LookupDto {
  Id?: number;
  id?: number;
  LookupId?: number;
  lookupId?: number;
  Code?: string;
  code?: string;
  Name?: string;
  name?: string;
  NameEN?: string;
  nameEN?: string;
  IsDraft?: boolean;
  isDraft?: boolean;
}

class LookupService {
  async createDraft(payload: Partial<Lookup>): Promise<Lookup> {
    const response = await api_no_token_Service.post<Lookup>(
      `/lookup/CreateDraft`,
      payload
    );
    return unwrapEntity<Lookup>(response);
  }

  async create(payload: Partial<Lookup>): Promise<Lookup> {
    const response = await apiService.post<Lookup>(`/lookup`, payload);
    return unwrapEntity<Lookup>(response);
  }

  async update(id: string, payload: Partial<Lookup>): Promise<Lookup> {
    const response = await apiService.put<Lookup>(`/lookup/${id}`, payload);
    return unwrapEntity<Lookup>(response);
  }

  async getByCode(code: string): Promise<LookupDto | null> {
    const res = await api_no_token_Service.get<LookupDto>(
      `/lookup/ByCode/${encodeURIComponent(code)}`
    );
    if ((res as { data?: LookupDto })?.data && (res as { success?: boolean })?.success !== false) {
      return (res as { data: LookupDto }).data;
    }
    return (res as LookupDto) ?? null;
  }

  async getByParentCode(
    parentCode: string,
    languageId: Language = Language.ENGLISH,
    includeLookupId?: string | number | null
  ): Promise<LookupDto[]> {
    const params = new URLSearchParams();
    params.set("languageId", String(languageId));
    if (
      includeLookupId !== undefined &&
      includeLookupId !== null &&
      String(includeLookupId) !== ""
    ) {
      params.set("includeLookupIds", String(includeLookupId));
    }
    const res = await api_no_token_Service.get<LookupDto[]>(
      `/lookup/GetByParentCode/${encodeURIComponent(parentCode)}?${params.toString()}`
    );
    return unwrapEntity<LookupDto[]>(res);
  }

  async getPageList(
    page: number = 1,
    limit: number = 100,
    languageId: number = 1,
    options?: {
      search?: string;
      parentLookupId?: string | number;
      isDraft?: boolean;
    }
  ): Promise<PaginatedResponse<Lookup>> {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(limit),
      languageId: String(languageId),
    });
    if (options?.search?.trim()) {
      params.set("search", options.search.trim());
    }
    if (options?.parentLookupId != null && String(options.parentLookupId).trim() !== "") {
      params.set("parentLookupId", String(options.parentLookupId));
    }
    if (options?.isDraft !== undefined) {
      params.set("isDraft", String(options.isDraft));
    }
    const response = await apiService.get<PaginatedResponse<Lookup>>(
      `/lookup/GetPageList?${params.toString()}`
    );

    return normalizePaginatedResponse<Lookup>(response, {
      page,
      limit,
      fallbackMessage: "Failed to load lookups",
    });
  }

  async getById(id: string): Promise<Lookup> {
    const response = await apiService.get<any>(`/lookup/${id}`);
    const payload: any = (response as any)?.data ?? response;

    const lookupData =
      payload?.data && typeof payload.data === "object"
        ? payload.data
        : typeof payload === "object"
        ? payload
        : null;

    if (lookupData) {
      return { ...lookupData } as Lookup;
    }

    return response as unknown as Lookup;
  }

  async getAll(): Promise<LookupDto[]> {
    const res = await apiService.get<LookupDto[]>(`/lookup`);
    return unwrapEntity<LookupDto[]>(res);
  }

  async remove(id: string): Promise<void> {
    const response = await apiService.delete(`/lookup/${id}`);
    unwrapOrThrow(response, "Delete failed");
  }

  async toggleDraft(id: string): Promise<void> {
    const response = await apiService.put(`/lookup/${id}/ToggleDraft`, {});
    unwrapOrThrow(response, "Toggle draft failed");
  }

  async getSiblings(id: string): Promise<{ id: number; code: string; value: string; isDraft?: boolean }[]> {
    const response = await apiService.get<any>(`/lookup/${id}/Siblings`);
    const data = (response as any)?.data ?? response;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  }

  async replace(id: string, replaceWithId: number): Promise<{ updatedCount: number }> {
    const response = await apiService.put<any>(`/lookup/${id}/Replace`, { replaceWithId });
    unwrapOrThrow(response, "Replace failed");
    const payload = (response as any)?.data ?? response;
    return { updatedCount: payload?.updatedCount ?? 0 };
  }
}

export const lookupService = new LookupService();
export default lookupService;
